import { Component, OnInit } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { DownloadInfo, DownloadStatus, DownloadType } from 'src/app/model/DownloadStatus';
import { DiscoveredProduct, Patch_TYPE } from 'src/app/model/Product';
import { UserInfo } from 'src/app/model/User';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { CatalogService } from 'src/app/service/catalog.service';
import { DataService } from 'src/app/service/data.service';
import { EventService } from 'src/app/service/event.service';
import { SoftwareInventoryService } from 'src/app/service/software-inventory.service';
import { Events, MsgPayLoad, WebsocketService } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { ActionMenuEvent, ActionMenuIcon, ActionMenuItem, ActionMenuItemType } from '../controls/actionmenu/action-menu.component';
import { ColumnSort, ColumnType, DataTableColumn, DataTableColumnFilter, DataTableRow, FilterType } from '../controls/datatable/datatable.component';
import { ModalMessageComponent, ModalMessageType } from '../controls/modal-message/modal-message.component';
import { Modal } from '../controls/modal/modal.service';
import { PageCount, PageEvent } from '../controls/pagination/pagination.component';
import { Tab, TabAlignment, TabColor } from '../controls/tabs/tabs.component';
import { ListItem } from '../dialogs/list-details/list-details.component';
import { ObjectDetailsComponent, ObjectDetailsTypes } from '../dialogs/object-details/object-details.component';
import { ZipDownloadProgressComponent } from '../dialogs/zip-download-progress/zip-download-progress.component';
import { DownloadItem } from '../products-overview/products-overview.component';

export enum ActivityDownloadTab {
  PRODUCTS,
  UPDATES,
  HOTFIXES,
  LANGUAGE_PACKS
}

@Component({
    selector: 'app-activities-downloads',
    templateUrl: './activities-downloads.component.html',
    styleUrls: ['./activities-downloads.component.css'],
    standalone: false
})
export class ActivitiesDownloadsComponent implements OnInit {

  ActivityDownloadTab = ActivityDownloadTab;
  productsTab = new Tab('Products', $localize`Products`, 0, true);
  updatesTab = new Tab('Updates', $localize`Updates`, 0, false);
  hotfixesTab = new Tab('Hotfixes', $localize`Hotfixes`, 0, false);
  languagesTab = new Tab('Language Packs', $localize`Language Packs`, 0, false);
  downloadTabs = [this.productsTab, this.updatesTab, this.hotfixesTab, this.languagesTab];
  productCols: Array<DataTableColumn>;
  updateCols: Array<DataTableColumn>;
  hotfixCols: Array<DataTableColumn>;
  langCols: Array<DataTableColumn>;
  loading: boolean = false;
  discoveredProducts: DiscoveredProduct[] = [];
  allProducts: DataTableRow[] = [];
  products: DataTableRow[] = [];
  allUpdates: DataTableRow[] = [];
  updates: DataTableRow[] = [];
  allHotfixes: DataTableRow[] = [];
  hotfixes: DataTableRow[] = [];
  allLangs: DataTableRow[] = [];
  langs: DataTableRow[] = [];
  activeTab: ActivityDownloadTab = ActivityDownloadTab.PRODUCTS;
  userAuthInfo: UserInfo;
  retryPkgData: any[]

  detailsId: string = 'otsc-activities-download-details-dialog';

  DownloadType = DownloadType;

  isOnlineMode: boolean = false;
  isProxyRequired: boolean = false;

  productPageInfo: PageEvent;
  updatesPageInfo: PageEvent;
  hotfixesPageInfo: PageEvent;
  languagesPageInfo: PageEvent;

  currentProductsPage: number = 0;
  currentUpdatesPage: number = 0;
  currentHotfixesPage: number = 0;
  currentLanguagesPage: number = 0;

  TabColor = TabColor;
  TabAlignment = TabAlignment;

  productFilterValue: string;
  productFilterField: string;
  productTotal: number = 0;
  updateFilterValue: string;
  updateFilterField: string;
  updateTotal: number = 0;
  hotfixFilterValue: string;
  hotfixFilterField: string;
  hotfixTotal: number = 0;
  langFilterValue: string;
  langFilterField: string;
  langTotal: number = 0;

  itemsToDownload: DownloadItem

  //subscription
  solutionInfoSubscription: Subscription;
  eventMessageSubscription: Subscription;

  constructor(protected eventService: EventService,
    protected authService: AuthenticationService,
    protected siService: SoftwareInventoryService,
    protected catalogSrv: CatalogService,
    protected dataService: DataService,
    protected wsService: WebsocketService,
    protected cookieSrv: CookieService,
    protected modal: Modal) {
    this.userAuthInfo = this.authService.getLoggedInUser();
  }

  ngOnInit(): void {

    this.productPageInfo = new PageEvent(1, 0, PageCount.TWENTYFIVE);
    this.updatesPageInfo = new PageEvent(1, 0, PageCount.TWENTYFIVE);
    this.hotfixesPageInfo = new PageEvent(1, 0, PageCount.TWENTYFIVE);
    this.languagesPageInfo = new PageEvent(1, 0, PageCount.TWENTYFIVE);

    this.productCols = Array<DataTableColumn>(
      new DataTableColumn("name", $localize`Name`, ColumnType.DOWNLOADPROGRESS, true, "47", ColumnSort.ASC, undefined,
        new DataTableColumnFilter(FilterType.STRING)),
      new DataTableColumn("version", $localize`Version`, ColumnType.STRING, false, "15", undefined, undefined,
        new DataTableColumnFilter(FilterType.STRING)),
      new DataTableColumn("size", $localize`Size`, ColumnType.FILESIZE, false, "10"),
      new DataTableColumn("user", $localize`User`, ColumnType.STRING, false, "10"),
      new DataTableColumn("date", $localize`Date`, ColumnType.DATEFROMNOW, false, "10"),
      new DataTableColumn("statusExt", $localize`Status`, ColumnType.STATUSEXT, false, "8")
    );

    this.updateCols = Array<DataTableColumn>(
      new DataTableColumn("product", $localize`Product`, ColumnType.DOWNLOADPROGRESS, false, "37", undefined, undefined,
        new DataTableColumnFilter(FilterType.STRING)),
      new DataTableColumn("version", $localize`Version`, ColumnType.STRING, true, "15", ColumnSort.ASC, undefined,
        new DataTableColumnFilter(FilterType.STRING)),
      new DataTableColumn("platformDisplayName", $localize`Platform`, ColumnType.STRING, false, "10"),
      new DataTableColumn("size", $localize`Size`, ColumnType.FILESIZE, false, "10"),
      new DataTableColumn("user", $localize`User`, ColumnType.STRING, false, "10"),
      new DataTableColumn("date", $localize`Date`, ColumnType.DATEFROMNOW, false, "10"),
      new DataTableColumn("statusExt", $localize`Status`, ColumnType.STATUSEXT, false, "8"),
    );

    this.hotfixCols = Array<DataTableColumn>(
      new DataTableColumn("product", $localize`Product`, ColumnType.DOWNLOADPROGRESS, false, "32", undefined, undefined,
        new DataTableColumnFilter(FilterType.STRING)),
      new DataTableColumn("name", $localize`Name`, ColumnType.STRING, true, "20", ColumnSort.ASC, undefined,
        new DataTableColumnFilter(FilterType.STRING)),
      new DataTableColumn("platformDisplayName", $localize`Platform`, ColumnType.STRING, false, "10"),
      new DataTableColumn("size", $localize`Size`, ColumnType.FILESIZE, false, "10"),
      new DataTableColumn("user", $localize`User`, ColumnType.STRING, false, "10"),
      new DataTableColumn("date", $localize`Date`, ColumnType.DATEFROMNOW, false, "10"),
      new DataTableColumn("statusExt", $localize`Status`, ColumnType.STATUSEXT, false, "8"),
    );

    this.langCols = Array<DataTableColumn>(
      new DataTableColumn("product", $localize`Product`, ColumnType.DOWNLOADPROGRESS, false, "22", undefined, undefined,
        new DataTableColumnFilter(FilterType.STRING)),
      new DataTableColumn("name", $localize`Name`, ColumnType.STRING, false, "15", undefined, undefined,
        new DataTableColumnFilter(FilterType.STRING)),
      new DataTableColumn("version", $localize`Version`, ColumnType.STRING, true, "15", ColumnSort.ASC, undefined,
        new DataTableColumnFilter(FilterType.STRING)),
      new DataTableColumn("platformDisplayName", $localize`Platform`, ColumnType.STRING, false, "10"),
      new DataTableColumn("size", $localize`Size`, ColumnType.FILESIZE, false, "10"),
      new DataTableColumn("user", $localize`User`, ColumnType.STRING, false, "10"),
      new DataTableColumn("date", $localize`Date`, ColumnType.DATEFROMNOW, false, "10"),
      new DataTableColumn("statusExt", $localize`Status`, ColumnType.STATUSEXT, false, "8"),
    );

    this.solutionInfoSubscription = this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      this.getDiscoveredProducts(newSolutionInfo.currentEnvironment.id);
    });

    this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
      switch (msg.eventType) {
        case Events.DownloadStarted:
          this.getDownloadInfo(false, false);
          Utils.showDownloadStartedDialog(DownloadType.HOTFIX, this.itemsToDownload.getTotalItems(), this.modal, this.cookieSrv);
          break;
        case Events.DownloadStatus:
          this.updateDownloadStatus(msg.body);
          break;
        case Events.DownloadCompleted:
          this.getDownloadInfo(false, false);
          break;
      }
    });

    this.getDownloadInfo(true, true);
  }

  ngOnDestroy(): void {
    if (this.solutionInfoSubscription !== undefined) {
      this.solutionInfoSubscription.unsubscribe();
    }
    if (this.eventMessageSubscription !== undefined) {
      this.eventMessageSubscription.unsubscribe();
    }
  }

  onSelectedTabChange(selectedIndex: number) {
    this.activeTab = selectedIndex;
  }

  getDiscoveredProducts(env: number): void {
    this.siService.getDiscoveredProducts(env).pipe(
      tap(resp => {
        this.discoveredProducts = resp.body;
      })).subscribe();
  }

  getDownloadInfo(isLoading: boolean, validate: boolean): void {
    this.loading = isLoading;
    this.siService.getDownloadInfo(validate).pipe(
      tap(resp => {
        this.setDownloadInfo(resp.body, this.loading);
      })
    ).subscribe();
  }

  setDownloadInfo(downloadInfo: DownloadInfo, isInit: boolean): void {
    if (downloadInfo !== undefined) {
      let tmpAllProducts: DataTableRow[] = [];
      let tmpAllUpdates: DataTableRow[] = [];
      let tmpAllHotfixes: DataTableRow[] = [];
      let tmpAllLangs: DataTableRow[] = [];

      if (downloadInfo.products !== undefined) {
        downloadInfo.products.forEach(product => {
          let productData = {
            'name': product.displayName,
            'version': product.version,
            'fileName': product.name,
            'size': product.downloadSize,
            'user': product.userName,
            'date': product.downloadedDateTime,
            'status': product.status,
            'statusExt': this.getDownloadStatus(product.status),
            'progress': product.percentage,
            'uid': product.uid,
            'error': this.getErrorText(product.error, product),
            'totalSize': product.totalSize,
            'pkgType': DownloadType.PRODUCT,
            'componentName': product.componentName,
            'downloadURL': product.downloadURL,
            'localDownloadUrl': product.localDownloadUrl,
          };
          let tmpProductRow = new DataTableRow(productData, this.getActionMenu(productData), false, false, false, "");
          tmpProductRow.selected = this.isProductSelected(product.uid);
          tmpAllProducts.push(tmpProductRow);
        });
      }

      if (downloadInfo.updates !== undefined) {
        downloadInfo.updates.forEach(update => {
          let updateData = {
            'product': update.isCatalog ? update.name : update.productName,
            'version': update.version,
            'fileName': update.fileName,
            'size': update.downloadByteSize,
            'platform': update.platform,
            'platformDisplayName': Utils.getLocalizedPlatform(update.platform),
            'user': update.userName,
            'date': update.downloadedDateTime,
            'status': update.status,
            'statusExt': this.getDownloadStatus(update.status),
            'progress': update.percentage,
            'uid': update.uid,
            'productGroupId': update.groupId,
            'error': this.getErrorText(update.error, update),
            'pkgId': update.pkgId,
            'totalSize': update.byteSize,
            'pkgType': DownloadType.UPDATE,
            'type': Patch_TYPE.UPDATE,
            'isCatalog': update.isCatalog,
            'localDownloadUrl': update.localDownloadUrl,
          };
          let tmpUpdateRow = new DataTableRow(updateData, this.getActionMenu(updateData), false, false, false, "");
          tmpUpdateRow.selected = this.isUpdateSelected(update.uid);
          tmpAllUpdates.push(tmpUpdateRow);
        });
      }

      if (downloadInfo.hotfixes !== undefined) {
        downloadInfo.hotfixes.forEach(hotfix => {
          let hotfixData = {
            'product': hotfix.productName,
            'name': hotfix.name,
            'fileName': hotfix.fileName,
            'size': hotfix.downloadByteSize,
            'platform': hotfix.platform,
            'platformDisplayName': hotfix.hotfixData['fileName'].includes('_LNXA') ? $localize`Linux` :  Utils.getLocalizedPlatform(hotfix.platform),
            'user': hotfix.userName,
            'date': hotfix.downloadedDateTime,
            'status': hotfix.status,
            'statusExt': this.getDownloadStatus(hotfix.status),
            'progress': hotfix.percentage,
            'uid': hotfix.uid,
            'productGroupId': hotfix.groupId,
            'error': this.getErrorText(hotfix.error, hotfix),
            'pkgId': hotfix.pkgId,
            'totalSize': hotfix.byteSize,
            'pkgType': DownloadType.HOTFIX,
            'type': Patch_TYPE.PATCH,
            'localDownloadUrl': hotfix.localDownloadUrl,
          };
          let tmpHotfixRow = new DataTableRow(hotfixData, this.getActionMenu(hotfixData), false, false, false, "");
          tmpHotfixRow.selected = this.isHotfixSelected(hotfix.uid);
          tmpAllHotfixes.push(tmpHotfixRow);
        });
      }
      
      if (downloadInfo.languages !== undefined) {
        downloadInfo.languages.forEach(language => {
          let langData = {
            'product': language.productName,
            'name': language.name,
            'version': language.version,
            'size': language.downloadByteSize,
            'platform': language.platform,
            'platformDisplayName': language.platform == 'MSWin32_64' ? $localize`Windows` : $localize`Linux`,
            'user': language.userName,
            'date': language.downloadedDateTime,
            'status': language.status,
            'statusExt': this.getDownloadStatus(language.status),
            'progress': language.percentage,
            'uid': language.uid,
            'productGroupId': language.groupId,
            'error': this.getErrorText(language.error, language),
            'pkgId': language.pkgId,
            'totalSize': language.byteSize,
            'pkgType': DownloadType.LANG,
            'localDownloadUrl': language.localDownloadUrl,
          };
          let tmpLangRow = new DataTableRow(langData, this.getActionMenu(langData), false, false, false, "");
          tmpLangRow.selected = this.isLangSelected(language.uid);
          tmpAllLangs.push(tmpLangRow);
        });
      }

      if (tmpAllProducts.length > 0) {
        this.allProducts = tmpAllProducts;
        if(!isInit){
          this.setProductPage(this.productPageInfo);
        }
      } else {
        this.allProducts = [];
        this.products = [];
      }

      if (tmpAllUpdates.length > 0) {
        this.allUpdates = tmpAllUpdates;
        if (!isInit) {
          this.setUpdatePage(this.updatesPageInfo);
        }
      } else {
        this.allUpdates = [];
        this.updates = [];
      }

      if (tmpAllHotfixes.length > 0) {
        this.allHotfixes = tmpAllHotfixes;
        if (!isInit) {
          this.setHotfixPage(this.hotfixesPageInfo);
        }
      } else {
        this.allHotfixes = [];
        this.hotfixes = [];
      }

      if (tmpAllLangs.length > 0) {
        this.allLangs = tmpAllLangs;
        if (!isInit) {
          this.setLangPage(this.languagesPageInfo);
        }
      } else {
        this.allLangs = [];
        this.langs = [];
      }

      this.productsTab.count = this.allProducts.length;
      this.updatesTab.count = this.allUpdates.length;
      this.hotfixesTab.count = this.allHotfixes.length;
      this.languagesTab.count = this.allLangs.length;
    }

    if (isInit) {
      this.setProductPage(this.productPageInfo);
      this.setUpdatePage(this.updatesPageInfo);
      this.setHotfixPage(this.hotfixesPageInfo);
      this.setLangPage(this.languagesPageInfo);
    }

    this.loading = false;
  }

  sortProducts(col: DataTableColumn) {
    this.productPageInfo.sortField = col.field;
    this.productPageInfo.sortOrder = col.sortDirection;
    this.setProductPage(this.productPageInfo);
  }

  sortUpdates(col: DataTableColumn) {
    this.updatesPageInfo.sortField = col.field;
    this.updatesPageInfo.sortOrder = col.sortDirection;
    this.setUpdatePage(this.updatesPageInfo);
  }

  sortHotfixes(col: DataTableColumn) {
    this.hotfixesPageInfo.sortField = col.field;
    this.hotfixesPageInfo.sortOrder = col.sortDirection;
    this.setHotfixPage(this.hotfixesPageInfo);
  }

  sortLanguages(col: DataTableColumn) {
    this.languagesPageInfo.sortField = col.field;
    this.languagesPageInfo.sortOrder = col.sortDirection;
    this.setLangPage(this.languagesPageInfo);
  }

  getActionMenu(downloadObj: any): ActionMenuItem[] {
    let infoAction = new ActionMenuItem("Information", $localize`Details`, ActionMenuIcon.DETAILS, downloadObj, false);
    infoAction.overrideDisabledRow = true;
    let actionMenu: ActionMenuItem[] = [infoAction];

    actionMenu.push(new ActionMenuItem("Delete", $localize`Delete`, ActionMenuIcon.DELETE, downloadObj, true));

    if (downloadObj.status == "COMPLETE" || downloadObj.status == "INSTALLED") {
      // bulk download if pkgType is not UPDATE and PRODUCT
      const isBulkDownload = downloadObj.pkgType !== Patch_TYPE.UPDATE && downloadObj.pkgType !== DownloadType.PRODUCT;
      actionMenu.push(new ActionMenuItem("Download", $localize`Download`, ActionMenuIcon.DOWNLOAD, downloadObj, isBulkDownload, undefined, ActionMenuItemType.DOWNLOADLINK, downloadObj.localDownloadUrl));
    }
    if (downloadObj.status == 'ERROR') {
      actionMenu.push(new ActionMenuItem("Retry", $localize`Retry`, ActionMenuIcon.RETRY, downloadObj, true));
    }
    return actionMenu;
  }

  getDownloadStatus(status: string) {
    let downloadStatus: any;
    switch (status) {
      case 'AVAILABLE':
        downloadStatus = { className: 'otsc-icon-status-available', altText: $localize`Available` };
        break;
      case 'OPEN':
      case 'INITIATING':
        downloadStatus = { className: 'otsc-icon-status-loading-spinner', altText: $localize`Initiating` };
        break;
      case 'VALIDATING':
        downloadStatus = { className: 'otsc-icon-status-loading-spinner', altText: $localize`Validating` };
        break;
      case 'DOWNLOAD':
        downloadStatus = { className: 'otsc-icon-status-loading-spinner', altText: $localize`Downloading` };
        break
      case 'COMPLETE':
        downloadStatus = { className: 'otsc-icon-status-success', altText: $localize`Downloaded` };
        break;
      case 'ERROR':
        downloadStatus = { className: 'otsc-icon-status-error', altText: $localize`Error` };
        break;
      case 'INSTALLED':
        downloadStatus = { className: 'otsc-icon-status-installed', altText: $localize`Installed` };
        break;
      default:
        downloadStatus = { className: 'otsc-icon-status-error', altText: $localize`Error` };
        break;
    }
    return downloadStatus;
  }

  actionMenuClicked(event: ActionMenuEvent): void {
    switch (event.name) {
      case 'Information':
        this.getInformation(event.data[0], true);
        break;
      case 'Delete':
        if (event.data[0].pkgType == DownloadType.PRODUCT) {
          this.canDeleteProducts(event.data);
        } else {
          this.canDeleteFiles(event.data);
        }
        break;
      case 'Download':
        if(event.data.length > 1){
          const downloadIds = event.data.map(item => item?.uid).filter(uid => !!uid);
          const pkgType = event.data[0]?.pkgType || DownloadType.HOTFIX;
          const idToNameMap: Record<string, string> = {};
          event.data.forEach(item => {
            if (item?.uid && item?.name) {
              idToNameMap[item.uid] = item.name;
            }
          });
          
          const sub: Subscription = this.modal.open(ZipDownloadProgressComponent, {
            data: {
              downloadIds,
              pkgType,
              idToNameMap
            },
            id: 'otsc-zip-download-progress-dialog'
          }).afterClosed.subscribe(() => {
            sub.unsubscribe();
          });
        }else{
          window.open(event.data[0].localDownloadUrl, '_blank');
        }
        break;
      case 'Retry':
        this.retryDownload(event.data);
        break;
    }
  }

  updateDownloadStatus(status: DownloadStatus): void {
    if (status.active != undefined && status.active.length > 0) {
      status.active.forEach(downloadPkg => {
        switch (downloadPkg.type) {
          case DownloadType.HOTFIX:
            let tmpAllHotfix = this.allHotfixes.find(hotfix => hotfix.data.uid == downloadPkg.uid);
            if (tmpAllHotfix !== undefined) {
              tmpAllHotfix.data.status = downloadPkg.status;
              tmpAllHotfix.data.progress = downloadPkg.percentage;
              tmpAllHotfix.data.downloadSize = downloadPkg.downloadSize;
              tmpAllHotfix.data.totalSize = downloadPkg.totalSize;
            } else {
              this.getDownloadInfo(false, false);
            }
            break;
          case DownloadType.UPDATE:
            let tmpAllUpdate = this.allUpdates.find(update => update.data.uid == downloadPkg.uid);
            if (tmpAllUpdate !== undefined) {
              tmpAllUpdate.data.status = downloadPkg.status;
              tmpAllUpdate.data.progress = downloadPkg.percentage;
              tmpAllUpdate.data.downloadSize = downloadPkg.downloadSize;
              tmpAllUpdate.data.totalSize = downloadPkg.totalSize;
            } else {
              this.getDownloadInfo(false, false);
            }
            break;
          case DownloadType.PRODUCT:
            let tmpAllProduct = this.allProducts.find(product => product.data.uid == downloadPkg.uid);
            if (tmpAllProduct !== undefined) {
              tmpAllProduct.data.status = downloadPkg.status;
              tmpAllProduct.data.progress = downloadPkg.percentage;
              tmpAllProduct.data.downloadSize = downloadPkg.downloadSize;
              tmpAllProduct.data.totalSize = downloadPkg.totalSize;
            } else {
              this.getDownloadInfo(false, false);
            }
            break;
          case DownloadType.LANG:
            let tmpAllLang = this.allLangs.find(lang => lang.data.uid == downloadPkg.uid);
            if (tmpAllLang !== undefined) {
              tmpAllLang.data.status = downloadPkg.status;
              tmpAllLang.data.progress = downloadPkg.percentage;
              tmpAllLang.data.downloadSize = downloadPkg.downloadSize;
              tmpAllLang.data.totalSize = downloadPkg.totalSize;
            } else {
              this.getDownloadInfo(false, false);
            }
            break;
        }
      });
    }
  }

  getInformation(data: any, show: boolean): any {
    switch (data.pkgType) {
      case DownloadType.PRODUCT:
        this.catalogSrv.getBinaryDependencies(data.uid).pipe(
          tap(resp => {
            if (show) {
              data.dependencies = resp.body;
              this.showInfo(ObjectDetailsTypes.PRODUCT, data);
            }
          })
        ).subscribe();
        break;
      case DownloadType.UPDATE:
        this.siService.getDownloadedUpdateData(data.uid).pipe(
          tap(resp => {
            if (show) {
              data.details = resp.body;
              data.description = resp.body.description ? resp.body.description : "";
              this.showInfo(ObjectDetailsTypes.UPDATE, data);
            } else {
              this.addRetryDetails(data.pkgId, resp.body);
            }
          })
        ).subscribe();
        break;
      case DownloadType.LANG:
        this.siService.getDownloadedLanguageData(data.pkgId).pipe(
          tap(resp => {
            if (show) {
              data.details = resp.body;
              this.showInfo(ObjectDetailsTypes.LANG_PACK, resp.body);
            } else {
              this.addRetryDetails(data.pkgId, resp.body);
            }
          })
        ).subscribe();
        break;
      case DownloadType.HOTFIX:
        this.siService.getDownloadedHotfixData(data.pkgId).pipe(
          tap(resp => {
            if (show) {
              data.details = resp.body;
              data.description = resp.body.description;
              this.showInfo(ObjectDetailsTypes.PATCH, data);
            } else {
              this.addRetryDetails(data.pkgId, resp.body);
            }
          })
        ).subscribe();
        break;
    }
  }

  showInfo(type: ObjectDetailsTypes, data: any) {
    this.modal.open(ObjectDetailsComponent, {
      data: {
        obj: data,
        type: type
      },
      id: this.detailsId
    });
  }

  onPageChange(pageEvent: PageEvent, type: DownloadType) {
    switch (type) {
      case DownloadType.PRODUCT:
        this.setProductPage(pageEvent);
        break;
      case DownloadType.UPDATE:
        this.setUpdatePage(pageEvent);
        break;
      case DownloadType.HOTFIX:
        this.setHotfixPage(pageEvent);
        break;
      case DownloadType.LANG:
        this.setLangPage(pageEvent);
        break;
    }
  }

  setProductPage(pageEvent: PageEvent) {
    let tmpProducts: DataTableRow[] = [];
    let sortField: string = pageEvent.sortField == undefined ? 'name' : pageEvent.sortField;
    let sortOrder: ColumnSort = pageEvent.sortOrder == undefined ? ColumnSort.ASC : pageEvent.sortOrder;
    let filteredProducts: DataTableRow[] = [];

    this.allProducts.sort((a, b) => (a.data[sortField] > b.data[sortField] ? (sortOrder == ColumnSort.ASC) ? 1 : -1 :
    (sortOrder == ColumnSort.ASC) ? -1 : 1));

    if(pageEvent.startingIndex >= this.allProducts.length){
      if(pageEvent.pagenum > 1){
        pageEvent.pagenum--;
      }
      if(pageEvent.startingIndex > 0){
        pageEvent.startingIndex -= pageEvent.itemsPerPage;
      }
    }

    if (this.productFilterValue !== undefined && this.productFilterValue !== "") {
      let tmpFilteredProducts: DataTableRow[] = [];
      this.allProducts.forEach(product => {
        if (product.data[this.productFilterField].toLowerCase().indexOf(this.productFilterValue.toLowerCase()) > -1) {
          tmpFilteredProducts.push(product);
        }
      });
      filteredProducts = tmpFilteredProducts;
    }else{
      filteredProducts = this.allProducts;
    }

    for (let i = pageEvent.startingIndex; i < filteredProducts.length; i++) {
      if (tmpProducts.length < pageEvent.itemsPerPage) {
        tmpProducts.push(filteredProducts[i]);
      } else {
        break;
      }
    }
    this.products = tmpProducts;
    this.productTotal = filteredProducts.length;
    pageEvent.sortField = sortField;
    pageEvent.sortOrder = sortOrder;
    this.productPageInfo = pageEvent;
    this.currentProductsPage = pageEvent.pagenum;
  }

  isProductSelected(uid: string): boolean {
    let isSelected: boolean = false;
    let product: DataTableRow;
    if (this.allProducts !== undefined && this.allProducts.length > 0) {
      product = this.allProducts.find(tmpProduct => tmpProduct.data.uid == uid);
      if (product !== undefined) {
        isSelected = product.selected;
      }
    }
    return isSelected;
  }

  setUpdatePage(pageEvent: PageEvent) {
    let tmpUpdates: DataTableRow[] = [];
    let sortField: string = pageEvent.sortField == undefined ? 'version' : pageEvent.sortField;
    let sortOrder: ColumnSort = pageEvent.sortOrder == undefined ? ColumnSort.ASC : pageEvent.sortOrder;
    let filteredUpdates: DataTableRow[] = [];

    this.allUpdates.sort((a, b) => (a.data[sortField] > b.data[sortField] ? (sortOrder == ColumnSort.ASC) ? 1 : -1 :
    (sortOrder == ColumnSort.ASC) ? -1 : 1));

    if(pageEvent.startingIndex >= this.allUpdates.length){
      if(pageEvent.pagenum > 1){
        pageEvent.pagenum--;
      }
      if(pageEvent.startingIndex > 0){
        pageEvent.startingIndex -= pageEvent.itemsPerPage;
      }
    }

    if (this.updateFilterValue !== undefined && this.updateFilterValue !== "") {
      let tmpFilteredUpdates: DataTableRow[] = [];
      this.allUpdates.forEach(update => {
        if (update.data[this.updateFilterField].toLowerCase().indexOf(this.updateFilterValue.toLowerCase()) > -1) {
          tmpFilteredUpdates.push(update);
        }
      });
      filteredUpdates = tmpFilteredUpdates;
    }else{
      filteredUpdates = this.allUpdates;
    }

    for (let i = pageEvent.startingIndex; i < filteredUpdates.length; i++) {
      if (tmpUpdates.length < pageEvent.itemsPerPage) {
        tmpUpdates.push(filteredUpdates[i]);
      } else {
        break;
      }
    }
    this.updates = tmpUpdates;
    this.updateTotal = filteredUpdates.length;
    pageEvent.sortField = sortField;
    pageEvent.sortOrder = sortOrder;
    this.updatesPageInfo = pageEvent;
    this.currentUpdatesPage = pageEvent.pagenum;
  }

  isUpdateSelected(uid: string): boolean {
    let isSelected: boolean = false;
    let update: DataTableRow;
    if (this.allUpdates !== undefined && this.allUpdates.length > 0) {
      update = this.allUpdates.find(tmpUpdate => tmpUpdate.data.uid == uid);
      if (update !== undefined) {
        isSelected = update.selected;
      }
    }
    return isSelected;
  }

  setHotfixPage(pageEvent: PageEvent) {
    let tmpHotfixes: DataTableRow[] = [];
    let sortField: string = pageEvent.sortField == undefined ? 'name' : pageEvent.sortField;
    let sortOrder: ColumnSort = pageEvent.sortOrder == undefined ? ColumnSort.ASC : pageEvent.sortOrder;
    let filteredHotfixes: DataTableRow[] = [];

    this.allHotfixes.sort((a, b) => (a.data[sortField] > b.data[sortField] ? (sortOrder == ColumnSort.ASC) ? 1 : -1 :
    (sortOrder == ColumnSort.ASC) ? -1 : 1));

    if(pageEvent.startingIndex >= this.allHotfixes.length){
      if(pageEvent.pagenum > 1){
        pageEvent.pagenum--;
      }
      if(pageEvent.startingIndex > 0){
        pageEvent.startingIndex -= pageEvent.itemsPerPage;
      }
    }

    if (this.hotfixFilterValue !== undefined && this.hotfixFilterValue !== "") {
      let tmpFilteredHotfixes: DataTableRow[] = [];
      this.allHotfixes.forEach(hotfix => {
        if (hotfix.data[this.hotfixFilterField].toLowerCase().indexOf(this.hotfixFilterValue.toLowerCase()) > -1) {
          tmpFilteredHotfixes.push(hotfix);
        }
      });
      filteredHotfixes = tmpFilteredHotfixes;
    }else{
      filteredHotfixes = this.allHotfixes;
    }

    for (let i = pageEvent.startingIndex; i < filteredHotfixes.length; i++) {
      if (tmpHotfixes.length < pageEvent.itemsPerPage) {
        tmpHotfixes.push(filteredHotfixes[i]);
      } else {
        break;
      }
    }
    this.hotfixes = tmpHotfixes;
    this.hotfixTotal = filteredHotfixes.length;
    pageEvent.sortField = sortField;
    pageEvent.sortOrder = sortOrder;
    this.hotfixesPageInfo = pageEvent;
    this.currentHotfixesPage = pageEvent.pagenum;
  }

  isHotfixSelected(uid: string): boolean {
    let isSelected: boolean = false;
    let hotfix: DataTableRow;
    if (this.allHotfixes !== undefined && this.allHotfixes.length > 0) {
      hotfix = this.allHotfixes.find(tmpHotfix => tmpHotfix.data.uid == uid);
      if (hotfix !== undefined) {
        isSelected = hotfix.selected;
      }
    }
    return isSelected;
  }

  setLangPage(pageEvent: PageEvent) {
    let tmpLangs: DataTableRow[] = [];
    let sortField: string = pageEvent.sortField == undefined ? 'version' : pageEvent.sortField;
    let sortOrder: ColumnSort = pageEvent.sortOrder == undefined ? ColumnSort.ASC : pageEvent.sortOrder;
    let filteredLangs: DataTableRow[] = [];

    this.allLangs.sort((a, b) => {
      if (a.data[sortField] == b.data[sortField]){
        return (a.data["name"] > b.data["name"]) ? 1 : -1;
      }else{
        return (a.data[sortField] > b.data[sortField]) ? (sortOrder == ColumnSort.ASC) ? 1 : -1 : (sortOrder == ColumnSort.ASC) ? -1 : 1
      }
    });

    if(pageEvent.startingIndex >= this.allLangs.length){
      if(pageEvent.pagenum > 1){
        pageEvent.pagenum--;
      }
      if(pageEvent.startingIndex > 0){
        pageEvent.startingIndex -= pageEvent.itemsPerPage;
      }
    }

    if (this.langFilterValue !== undefined && this.langFilterValue !== "") {
      let tmpFilteredLangs: DataTableRow[] = [];
      this.allLangs.forEach(lang => {
        if (lang.data[this.langFilterField].toLowerCase().indexOf(this.langFilterValue.toLowerCase()) > -1) {
          tmpFilteredLangs.push(lang);
        }
      });
      filteredLangs = tmpFilteredLangs;
    }else{
      filteredLangs = this.allLangs;
    }

    for (let i = pageEvent.startingIndex; i < filteredLangs.length; i++) {
      if (tmpLangs.length < pageEvent.itemsPerPage) {
        tmpLangs.push(filteredLangs[i]);
      } else {
        break;
      }
    }
    this.langs = tmpLangs;
    this.langTotal = filteredLangs.length;
    pageEvent.sortField = sortField;
    pageEvent.sortOrder = sortOrder;
    this.languagesPageInfo = pageEvent;
    this.currentLanguagesPage = pageEvent.pagenum;
  }

  isLangSelected(uid: string): boolean {
    let isSelected: boolean = false;
    let lang: DataTableRow;
    if (this.allLangs !== undefined && this.allLangs.length > 0) {
      lang = this.allLangs.find(tmpLang => tmpLang.data.uid == uid);
      if (lang !== undefined) {
        isSelected = lang.selected;
      }
    }
    return isSelected;
  }

  getErrorText(error: string, downloadObj: any): string {
    let errorText = error;
    if(downloadObj.name == undefined || downloadObj.name == ""){
      downloadObj.name = downloadObj.displayName;
    }
    switch (error) {
      case 'DOWNLOAD_ERROR_CANCELLED':
        errorText = $localize`Downloading of ${downloadObj.name} has failed, it was cancelled by the user.`;
        break;
      case 'DOWNLOAD_ERROR_LOGIN':
        errorText = $localize`Authentication has failed during download of ${downloadObj.name}, please verify your user name and password.`;
        break;
      case 'DOWNLOAD_ERROR_TEST_EULA':
        errorText = $localize`You must accept the end user license agreement before you can start the download. Please click <a href='${downloadObj.downloadURL}' target='_blank'>here</a> to go to My Support and accept the agreement.`;
        break;
      case 'DOWNLOAD_ERROR_EULA':
        errorText = $localize`Downloading of ${downloadObj.name} has failed, the end user license agreement must be accepted.`;
        break;
      case 'DOWNLOAD_ERROR_GENERAL':
        errorText = $localize`One or more files failed to download.`;
        break;
      case 'DOWNLOAD_ERROR_HASHCODE':
        errorText = $localize`Downloading ${downloadObj.name} has failed, the file does not match the manifest records.`;
        break;
      case 'DOWNLOAD_ERROR_LANGPACK_NOT_FOUND':
        errorText = $localize`Downloading '${downloadObj.name}' language pack has failed, the selected language pack was not found.`;
        break;
      case 'DOWNLOAD_ERROR_LOGIN_UNKNOWN':
        errorText = $localize`Please verify your user name and password.`;
        break;
      case 'DOWNLOAD_ERROR_STATUS_400':
        errorText = $localize`Error downloading ${downloadObj.name}\: 400 Bad request.`;
        break;
      case 'DOWNLOAD_ERROR_STATUS_401':
        errorText = $localize`Error downloading ${downloadObj.name}\: 401 Unauthorized.`;
        break;
      case 'DOWNLOAD_ERROR_STATUS_403':
        errorText = $localize`Error downloading ${downloadObj.name}\: 403 Forbidden.`;
        break;
      case 'DOWNLOAD_ERROR_STATUS_404':
        errorText = $localize`Error downloading ${downloadObj.name}\: 404 Not found.`;
        break;
      case 'DOWNLOAD_ERROR_STATUS_405':
        errorText = $localize`Error downloading ${downloadObj.name}\: 405 Method not allowed.`;
        break;
      case 'DOWNLOAD_ERROR_STATUS_407':
        errorText = $localize`Error downloading ${downloadObj.name}\: 407 Proxy authentication required.`;
        break;
      case 'DOWNLOAD_ERROR_STATUS_408':
        errorText = $localize`Error downloading ${downloadObj.name}\: 408 Request timeout.`;
        break;
      case 'DOWNLOAD_ERROR_STATUS_500':
        errorText = $localize`Error downloading ${downloadObj.name}\: 500 Internal server error.`;
        break;
      case 'DOWNLOAD_ERROR_STATUS_502':
        errorText = $localize`Error downloading ${downloadObj.name}\: 502 Bad gateway.`;
        break;
      case 'DOWNLOAD_ERROR_STATUS_503':
        errorText = $localize`Error downloading ${downloadObj.name}\: 503 Service unavailable.`;
        break;
      case 'DOWNLOAD_ERROR_UNKNOWN':
        errorText = $localize`Error downloading ${downloadObj.name}, please try again later.`;
        break;
      case 'FILE_NOT_FOUND':
        errorText = $localize`The file could not be found.`;
        break;
    }
    
    return errorText;
  }

  canDeleteProducts(pkgs: any[]) {
    let inUsePkgs: any[] = [];
    let inUsePlanPkgs: any[] = [];
    let uids: string[] = [];
    let listItems: ListItem[] = [];
    pkgs.forEach(pkg => {
      uids.push(pkg.uid);
    });
    this.siService.canDeleteProducts(uids).pipe(
      tap(resp => {
        let inUseResult: any[] = resp.body;

        if (inUseResult == undefined || inUseResult.length == 0) {
          this.showDeleteFiles(pkgs);
        } else {
          inUseResult.forEach(result => {
            let tmpPkg = pkgs.find(element => element.uid == result.uid);
            if (result.inUse) {
              inUsePkgs.push(tmpPkg);
            }

            if (result.planCount > 0) {
              inUsePlanPkgs.push(tmpPkg);
            }
          });

          if (inUsePkgs.length == 0 && inUsePlanPkgs.length == 0) {
            this.showDeleteFiles(pkgs);
          } else {
            let messageContent: string = '';
            if (inUsePkgs.length > 0) {
              messageContent = inUsePkgs.length > 1 ?
                $localize`The selected products below cannot be deleted at this time, they are currently in use.  Please try again later.` :
                $localize`The selected product below cannot be deleted at this time, it is currently in use.  Please try again later.`;
              inUsePkgs.forEach(inUsePkg => {
                let item = new ListItem();
                item.msg = inUsePkg.name + ' - ' + inUsePkg.version;
                listItems.push(item);
              });
              this.modal.open(ModalMessageComponent, {
                data: {
                  title: $localize`Delete`,
                  type: ModalMessageType.Question,
                  message: messageContent,
                  primaryBtn: $localize`Delete`,
                  width: '550px',
                  list: listItems
                },
                id: 'otsc-downloads-delete-in-plan-product-msg'
              });
            } else {
              messageContent = inUsePlanPkgs.length > 1 ?
                $localize`The selected products below are referenced in one or more plans.  If you delete this product the corresponding plans will not function.  Are you sure you want to delete these products?` :
                $localize`The selected product below is referenced in one or more plans.  If you delete this product the corresponding plans will not function.  Are you sure you want to delete this product?`;
              inUsePlanPkgs.forEach(inPlanPkg => {
                let item = new ListItem();
                item.msg = inPlanPkg.name + ' - ' + inPlanPkg.version;
                listItems.push(item);
              });

              const sub: Subscription =
                this.modal.open(ModalMessageComponent, {
                  data: {
                    title: $localize`Delete`,
                    type: ModalMessageType.Question,
                    message: messageContent,
                    primaryBtn: $localize`Delete`,
                    secondaryBtn: $localize`Cancel`,
                    width: '550px',
                    list: listItems
                  },
                  id: 'otsc-downloads-delete-in-plan-product-msg'
                }).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
                  if (isPrimaryBtnClicked) {
                    this.deleteFiles(pkgs);
                  }
                  sub.unsubscribe();
                });
            }
          }
        }
      })
    ).subscribe();
  }

  showDeleteFiles(pkgs: any[]): void {
    let itemName: string;

    if (pkgs.length == 1) {
      if (pkgs[0].pkgType == DownloadType.PRODUCT) {
        itemName = pkgs[0].componentName + ' ' + pkgs[0].version;
      } else if (pkgs[0].pkgType == DownloadType.LANG) {
        itemName = pkgs[0].name + " " + pkgs[0].version;
      } else if (pkgs[0].pkgType == DownloadType.UPDATE) {
        itemName = pkgs[0].product + " " + pkgs[0].version;
      } else {
        itemName = pkgs[0].name;
      }
    }

    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Delete`,
          message: pkgs.length === 1 ? $localize`${itemName} will be deleted. Delete selected item?` :
            $localize`${pkgs.length} items will be deleted. Delete selected items?`,
          primaryBtn: $localize`Delete`,
          secondaryBtn: $localize`Cancel`,
          width: '350px'
        },
        id: 'otsc-downloads-delete-item-msg'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.deleteFiles(pkgs);
        }
        sub.unsubscribe();
      });
  }

  canDeleteFiles(pkgs: any[]) {
    let invalidEnvs: any[] = [];

    pkgs.forEach(pkg => {
      if (!this.isValidProductInEnv(pkg.productGroupId, pkg.platform)) {
        invalidEnvs.push(pkg);
      }
    });

    if (invalidEnvs.length > 0) {
      let messageContent: string = invalidEnvs.length > 1 ?
        $localize`The following list of selected files are for products not currently registered in this environment.  Deleting these files could negatively affect other environments.  <br/><br/>Are you sure you want to remove the selected ${invalidEnvs.length} files?` :
        $localize`The following selected file is for a product not currently registered in this environment.  Deleting this file could negatively affect other environments.  <br/><br/>Are you sure you want to remove the selected file?`;
      let listItems: ListItem[] = [];
      invalidEnvs.forEach(invalidEnv => {
        let tmpItem: ListItem = new ListItem();
        if (invalidEnv.pkgType === DownloadType.UPDATE) {
          tmpItem.msg = invalidEnv.product + ' - ' + invalidEnv.version;
        } else if (invalidEnv.pkgType === DownloadType.LANG) {
          tmpItem.msg = invalidEnv.product + ' - ' + invalidEnv.name + ' ' + invalidEnv.version;
        } else if (invalidEnv.pkgType === DownloadType.HOTFIX) {
          tmpItem.msg = invalidEnv.product + ' - ' + invalidEnv.name;
        }
        listItems.push(tmpItem);
      });
      const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Delete`,
            type: ModalMessageType.Question,
            message: messageContent,
            primaryBtn: $localize`Delete`,
            secondaryBtn: $localize`Cancel`,
            width: '550px',
            list: listItems
          },
          id: 'otsc-download-delete-in-use-product-msg'
        }).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
          if (isPrimaryBtnClicked) {
            this.deleteFiles(pkgs);
          }
          sub.unsubscribe();
        });
    } else {
      this.showDeleteFiles(pkgs);
    }
  }

  isValidProductInEnv(groupId: number, platform: string): boolean {
    let valid: boolean = false;
    let productGroup = this.discoveredProducts.find(productGroup => productGroup.groupId == groupId);
    if (productGroup !== undefined) {
      let tmpPlatform = productGroup.products.find(product => product.platform.toLowerCase() === platform.toLowerCase());
      if (tmpPlatform !== undefined) {
        valid = true;
      }
    }
    return valid;
  }

  deleteFiles(pkgs: any) {
    let uids: string[] = [];
    let isProduct: boolean = false;

    pkgs.forEach((data) => {
      uids.push(data.uid);
      if (data.pkgType == DownloadType.PRODUCT) {
        isProduct = true;
      }
    });

    if (isProduct) {
      this.siService.removeDownloadedProductFiles(uids).pipe(
        tap(resp => {
          let itemName: string;

          if (pkgs.length == 1) {
            itemName = pkgs[0].componentName + ' ' + pkgs[0].version;
          }

          let msg = new MsgPayLoad(undefined, {
            message: pkgs.length === 1 ? $localize`${itemName} was successfully deleted.` :
              $localize`${pkgs.length} items were successfully deleted.`
          });
          msg.setEvent(Events.SuccessMsg);

          Utils.addHeaderMessageBanner(this.eventService, msg);
        }),
        finalize(() => {
          this.getDownloadInfo(false, false);
        })
      ).subscribe();
    } else {
      this.siService.removeDownloadFiles(uids, true).pipe(
        tap(resp => {
          let itemName: string;

          if (pkgs.length == 1) {
            if (pkgs[0].pkgType == DownloadType.LANG) {
              itemName = pkgs[0].name + " " + pkgs[0].version;
            } else if (pkgs[0].pkgType == DownloadType.UPDATE) {
              itemName = pkgs[0].product + " " + pkgs[0].version;
            } else {
              itemName = pkgs[0].name;
            }
          }

          let msg = new MsgPayLoad(undefined, {
            message: pkgs.length === 1 ? $localize`${itemName} was successfully deleted.` :
              $localize`${pkgs.length} items were successfully deleted.`
          });
          msg.setEvent(Events.SuccessMsg);

          Utils.addHeaderMessageBanner(this.eventService, msg);
        }),
        finalize(() => {
          this.getDownloadInfo(false, false);
        })
      ).subscribe();
    }
  }

  setUserData(): void {
    var user: UserInfo = this.authService.getLoggedInUser();
    this.isOnlineMode = user.settings.isOnlineMode;
    this.isProxyRequired = user.settings.proxyPrompt;
  }

  retryDownload(pkgData: any[]): void {
    let infoNeeded: boolean = false;
    this.retryPkgData = [];

    pkgData.forEach(pkg => {
      if (pkg.status == "ERROR") {
        this.retryPkgData.push(pkg);
        if (pkg.pkgType == DownloadType.HOTFIX || pkg.pkgType == DownloadType.UPDATE ||
          pkg.pkgType == DownloadType.LANG) {
          infoNeeded = true;
          this.getInformation(pkg, false);
        }
      }
    });

    if (infoNeeded) {
      setTimeout(() => {
        this.waitForPackageData(0);
      }, 1000);
    } else {
      this.setCredentials(this.retryPkgData);
    }
  }

  addRetryDetails(pkgId: number, details: any) {
    for (let i = 0; i < this.retryPkgData.length; i++) {
      if (this.retryPkgData[i].pkgId == pkgId) {
        this.retryPkgData[i].details = details;
        break;
      }
    }
  }

  waitForPackageData(count: number): void {
    let isWaiting: boolean = false;
    this.retryPkgData.forEach(pkg => {
      if ((pkg.pkgType == DownloadType.HOTFIX || pkg.pkgType == DownloadType.UPDATE ||
        pkg.pkgType == DownloadType.LANG) && pkg.details == undefined) {
        isWaiting = true;
      }
    });

    if (isWaiting && count < 20) {
      setTimeout(() => {
        this.waitForPackageData(count++);
      }, 1000);
    } else {
      if (count != 20) {
        this.setCredentials(this.retryPkgData);
      }
    }
  }

  setCredentials(pkgData: any[]) {
    let productSummaryText: string = "";
    this.itemsToDownload = new DownloadItem();

    pkgData.forEach(pkg => {
      switch (pkg.pkgType) {
        case DownloadType.PRODUCT:
          this.itemsToDownload.products.push(pkg);
          productSummaryText += pkg.componentName + " " + pkg.version + ", ";
          break;
        case DownloadType.HOTFIX:
          this.itemsToDownload.patches.push(pkg);
          productSummaryText += (pkg.version == undefined) ? pkg.name + ", " : pkg.name + " " + pkg.version + ", ";
          break;
        case DownloadType.UPDATE:
          this.itemsToDownload.updates.push(pkg);
          productSummaryText += pkg.product + " " + pkg.version + ", ";
          break;
        case DownloadType.LANG:
          this.itemsToDownload.languages.push(pkg);
          productSummaryText += (pkg.version == undefined) ? pkg.name + ", " : pkg.name + " " + pkg.version + ", ";
          break;
      }
    });

    productSummaryText = (pkgData.length > 3) ? pkgData.length + " components selected." : productSummaryText.substring(0, productSummaryText.length - 2)
    this.setUserData();
    Utils.startDownload(this.isProxyRequired, 0, "", this.itemsToDownload, productSummaryText, this.modal, this.dataService, this.siService);
  }

  onProductFilterChanged(event: any): void {
    this.productFilterValue = event.value;
    this.productFilterField = event.field;
    this.setProductPage(new PageEvent(1, 0, this.productPageInfo.itemsPerPage));
  }

  onUpdateFilterChanged(event: any): void {
    this.updateFilterValue = event.value;
    this.updateFilterField = event.field;
    this.setUpdatePage(new PageEvent(1, 0, this.updatesPageInfo.itemsPerPage));
  }

  onHotfixFilterChanged(event: any): void {
    this.hotfixFilterValue = event.value;
    this.hotfixFilterField = event.field;
    this.setHotfixPage(new PageEvent(1, 0, this.hotfixesPageInfo.itemsPerPage));
  }

  onLangFilterChanged(event: any): void {
    this.langFilterValue = event.value;
    this.langFilterField = event.field;
    this.setLangPage(new PageEvent(1, 0, this.languagesPageInfo.itemsPerPage));
  }

  rowClick(event: any): void{
    this.getInformation(event.data, true);
  }
}
